import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, Mail, BellRing, ShieldAlert, ArrowRight } from 'lucide-react';

const FREQ_LABEL = { immediate: 'Immediate', daily: 'Daily', weekly: 'Weekly', off: 'Off (no saved-search alerts)' };

const EmailPreferences = ({ showJobAlerts = true }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/users/email-preferences`);
      setPrefs(res.data);
    } catch (e) {
      toast.error('Could not load your email preferences.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/users/email-preferences`, patch);
      setPrefs((p) => ({ ...p, ...res.data }));
      toast.success('Email preferences updated.');
    } catch (e) {
      toast.error('Failed to update preferences.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16 text-gray-500" data-testid="email-prefs-loading"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="email-prefs-tab">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-primary" /></div>
          <div className="flex-1">
            <h3 className="font-serif text-lg font-bold text-primary">Email Notifications</h3>
            <p className="text-sm text-gray-500 mt-1">Optional updates — {showJobAlerts ? 'job digests, saved-search alerts' : 'applicant summaries and platform updates'}, and helpful tips. Turning this off never affects your account, and we'll still send essential account emails (password, security).</p>
          </div>
          <Switch checked={!!prefs.email_notifications} disabled={saving} onCheckedChange={(v) => save({ email_notifications: v })} data-testid="email-notifications-switch" />
        </div>
      </div>

      {showJobAlerts && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0"><BellRing className="w-5 h-5 text-secondary" /></div>
            <div className="flex-1">
              <h3 className="font-serif text-lg font-bold text-primary">Job Alert Frequency</h3>
              <p className="text-sm text-gray-500 mt-1">Current: <span className="font-semibold text-primary">{FREQ_LABEL[prefs.job_alert_frequency] || prefs.job_alert_frequency}</span> ({prefs.saved_search_count} saved {prefs.saved_search_count === 1 ? 'search' : 'searches'})</p>
              <p className="text-sm text-gray-500 mt-1">Frequency is set per saved search. Manage them in the Saved Searches &amp; Alerts tab.</p>
              <Link to="/dashboard?tab=alerts"><Button variant="outline" size="sm" className="mt-3" data-testid="manage-alerts-link">Manage Saved Searches <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            </div>
          </div>
        </div>
      )}

      {prefs.email_unsubscribed ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="resubscribe-box">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800">You're unsubscribed from all optional emails</h3>
              <p className="text-sm text-red-700 mt-1">You used a one-click unsubscribe link, so you won't receive any optional emails until you resubscribe.</p>
              <Button onClick={() => save({ resubscribe: true, email_notifications: true })} disabled={saving} className="mt-3 bg-primary hover:bg-primary/90 text-white" data-testid="resubscribe-btn">Resubscribe</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <button onClick={() => save({ unsubscribe_all: true, email_notifications: false })} disabled={saving} className="text-sm text-gray-400 hover:text-red-600 underline" data-testid="unsubscribe-all-btn">Unsubscribe from all optional emails</button>
        </div>
      )}
    </div>
  );
};

export default EmailPreferences;
